import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Stethoscope, Phone, Building2, Anchor, Bell } from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { fmtDate, calcAge, istToday } from "../api";
import StatCards from "../components/StatCards";
import { Card, PageHeader, StatusBadge, EmptyState, Spinner, Badge } from "../components/ui";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    const fetchAll = () => {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
      const today = istToday();
      api.get(`/candidates?date_from=${today}&date_to=${today}`)
        .then((r) => setRecent([...r.data].sort((a, b) => (b.activity_at || b.updated_at || "").localeCompare(a.activity_at || a.updated_at || "")).slice(0, 3)))
        .catch(() => setRecent([]));
    };
    fetchAll();
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  }, [user.role]);

  if (user.role === "pandi") return <Navigate to="/pandi" replace />;

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user.full_name.split(" ")[0]}`}
        subtitle={user.role === "client" ? "Overview of your cases" : user.organization_name ? `${user.organization_name} — overview of your cases` : "System-wide overview"}
      />
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400" data-testid="stats-daily-note">
        Today's figures — counters reset every day at midnight
      </p>
      <StatCards stats={stats} hide={user.role === "accounts" ? ["pending"] : []} />

      {user.role === "doctor" && stats?.needs_review > 0 && (
        <Card className="mt-6 flex items-center justify-between border-teal bg-teal-light/50 p-5" data-testid="review-queue-banner">
          <div className="flex items-center gap-3">
            <Stethoscope className="text-teal" size={22} />
            <p className="text-sm font-semibold text-navy">
              {stats.needs_review} case{stats.needs_review > 1 ? "s" : ""} awaiting your review
            </p>
          </div>
          <Link to="/candidates?review=1" className="flex items-center gap-1 text-sm font-bold text-teal transition-colors hover:text-navy">
            Review now <ArrowRight size={15} />
          </Link>
        </Card>
      )}

      <div className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-slate-900">Recent Candidates</h3>
          <Link to="/candidates" className="flex items-center gap-1 text-sm font-semibold text-teal transition-colors hover:text-navy" data-testid="view-all-candidates-link">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        {recent === null ? (
          <Card><div className="flex justify-center py-12"><Spinner /></div></Card>
        ) : recent.length === 0 ? (
          <Card><EmptyState text="No candidates yet" /></Card>
        ) : (
          <div className="stagger grid grid-cols-1 gap-4 md:grid-cols-3">
            {recent.map((c) => (
              <Card key={c.visit_id || c.id} className="p-6 transition-transform hover:-translate-y-1 hover:shadow-sm" data-testid="recent-candidate-card">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-heading text-lg font-bold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-500">
                      {c.rank}{calcAge(c.dob) !== null ? ` · ${calcAge(c.dob)} yrs` : ""} · ID {c.company_id}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {c.has_new_remark && <Bell size={14} fill="currentColor" className="bell-ring text-red-600" data-testid={`new-remark-bell-${c.visit_id || c.id}`} />}
                    <StatusBadge status={c.status} />
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                  {user.role !== "client" && <p className="flex items-center gap-2"><Building2 size={13} className="text-teal" /> {c.client_name}</p>}
                  {user.role === "client" && c.vessel && <p className="flex items-center gap-2" data-testid={`dash-vessel-${c.visit_id || c.id}`}><Anchor size={13} className="text-teal" /> {c.vessel}</p>}
                  <p className="flex items-center gap-2"><Anchor size={13} className="text-teal" /> {c.clinic_name}</p>
                  <p className="flex items-center gap-2"><Phone size={13} className="text-teal" /> {c.mobile}</p>
                  {c.reviewed_by && c.status !== "pending" && (
                    <p className="flex items-center gap-2" data-testid={`dash-reviewed-by-${c.id}`}><Stethoscope size={13} className="text-teal" /> {c.reviewed_by}</p>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-xs text-slate-400">{fmtDate(c.updated_at)}</span>
                  {c.status === "pending" && c.report_uploaded !== undefined && (
                    <Badge color={c.report_uploaded ? "teal" : "amber"}>{c.report_uploaded ? "Report Uploaded" : "Report Pending"}</Badge>
                  )}
                  <Link to={`/candidates/${c.id}`} className="flex items-center gap-1 text-sm font-bold text-teal transition-colors hover:text-navy" data-testid={`open-recent-${c.visit_id || c.id}`}>
                    Open <ArrowRight size={13} />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
